import React, { useState, useEffect } from 'react';
import {
  Avatar,
  Button,
  Grid,
  LinearProgress,
  Stack,
  Typography,
  Box,
  Paper,
  Chip,
  Divider
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { api as axios } from '../../api';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import url from '../../serverUrl';
import Transactions from '../Transactions/transaction.table';
import ProChart from '../../components/ProChart/ProChart';
import { logoWrapper } from '../../utils';
import SwapDiv from '../../components/SwapDiv';
import TreasuryBackingPublic from '../../components/TreasuryBackingPublic';
import {
  SENIORITY_CODES,
  YIELD_TYPE_CODES,
  YIELD_RATE_CODES,
  REDEMPTION_FREQUENCY_CODES,
  EARLY_REDEMPTION_CODES,
  CONVERSION_MECHANISM_CODES,
  CONVERSION_WINDOW_CODES,
  MANDATORY_HOLD_CODES,
  YTM_CODES
} from '../../screens/editForms/dcnCodeDictionary';

const formatPrice = (value) => {
  return value > 1 ? value?.toFixed(5) : value?.toFixed(5);
};

const DetailRow = ({ label, value, color = '#f0f0f0' }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      py: 0.75,
      gap: 2
    }}
  >
    <Typography
      variant="body2"
      sx={{ fontWeight: 600, color: '#fff', minWidth: '40%' }}
    >
      {label}:
    </Typography>
    <Typography
      variant="body2"
      sx={{ color, textAlign: 'right', flex: 1, wordBreak: 'break-word' }}
    >
      {value !== null && value !== undefined && value !== '' ? value : 'N/A'}
    </Typography>
  </Box>
);

const DetailBlock = ({ label, value }) => (
  <Box sx={{ mb: 2 }}>
    <Typography
      variant="body2"
      sx={{ fontWeight: 600, color: '#fff', mb: 0.5 }}
    >
      {label}:
    </Typography>
    <Typography
      variant="body2"
      sx={{ color: '#f0f0f0', wordBreak: 'break-word', lineHeight: 1.6 }}
    >
      {value !== null && value !== undefined && value !== '' ? value : 'N/A'}
    </Typography>
  </Box>
);

const resolveLabel = (code, list) => {
  if (!code || !Array.isArray(list)) return 'N/A';
  return list.find((i) => i.code === code)?.label || code;
};

export default function DCNPage() {
  const [data, setData] = useState({});
  const { identitySymbol } = useParams();
  const tokenSymbol = identitySymbol;
  const [assets, setAssets] = useState([{ pecu: 0, tokens: [] }]);
  const [token, setToken] = useState(data);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (data && data.id) {
      setToken(data);
    }
  }, [data]);

  useEffect(() => {
    if (tokenSymbol) {
      setLoading(true);
      axios
        .get(`${url}/dcn/${tokenSymbol}`)
        .then((res) => {
          if (res.data) {
            const token = res.data;
            setData(token);
          }
          setLoading(false);
        })
        .catch((err) => {
          setLoading(false);
        });

      axios
        .get(`${url}/dcn/balance/${tokenSymbol}`)
        .then((res) => {
          if (res.data) {
            setAssets(res.data);
          }
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }, [tokenSymbol]);

  const priceChange = data.currentPrice - data.price1hour;
  const priceChangePercent = Math.abs((priceChange / data.currentPrice) * 100);
  const isPositive = priceChange > 0;

  return (
    <>
      <Helmet>
        <title>{`${identitySymbol} ${
          data?.currentPrice?.toFixed(5) ?? ''
        }`}</title>
      </Helmet>

      {loading && (
        <Box>
          <LinearProgress sx={{ backgroundColor: 'grey' }} />
        </Box>
      )}

      {!loading && (
        <Box sx={{ p: { xs: 2, md: 4 }, ml: { xs: 4 } }}>
          <Grid container spacing={3} sx={{ ml: '-55px' }}>
            <SwapDiv token={tokenSymbol} />

            {/* Left Column - Token Info */}
            <Grid item xs={12} md={5} mt={4} sx={{ order: { xs: 1, md: 0 } }}>
              <Paper
                elevation={0}
                sx={{
                  backgroundColor: 'rgba(4, 10, 8, 0.72)',
                  borderRadius: 3,
                  p: 3,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 3
                }}
              >
                {/* Token Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar
                    src={logoWrapper(token?.logo, token)}
                    alt={token.symbol}
                    sx={{ width: 56, height: 56 }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h5"
                      sx={{ color: 'white', fontWeight: 600, mb: 0.5 }}
                    >
                      {token?.identitySymbol}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                      {token?.name}
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

                {/* Price Section */}
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#9ca3af',
                      mb: 1.5,
                      textTransform: 'uppercase',
                      fontSize: '0.75rem',
                      fontWeight: 600
                    }}
                  >
                    {token?.symbol} Price
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: 2,
                      flexWrap: 'wrap'
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{ color: 'white', fontWeight: 600 }}
                    >
                      ${formatPrice(data?.currentPrice)}
                    </Typography>
                    {data.currentPrice && data.price1hour && (
                      <Chip
                        icon={
                          isPositive ? (
                            <ArrowUpwardIcon sx={{ fontSize: '16px' }} />
                          ) : (
                            <ArrowDownwardIcon sx={{ fontSize: '16px' }} />
                          )
                        }
                        label={`${priceChangePercent.toFixed(2)}%`}
                        size="medium"
                        sx={{
                          backgroundColor: isPositive
                            ? 'rgba(16, 185, 129, 0.15)'
                            : 'rgba(239, 83, 80, 0.15)',
                          color: isPositive ? '#10B981' : '#EF5350',
                          fontWeight: 700,
                          fontSize: '0.875rem',
                          '& .MuiChip-icon': {
                            color: 'inherit'
                          }
                        }}
                      />
                    )}
                  </Box>
                </Box>

                <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

                {/* Key Metrics */}
                <Box
                  sx={{
                    border: '0px solid #10B981',
                    borderRadius: 2,
                    p: 2.5,
                    backgroundColor: 'rgba(16, 185, 129, 0.03)'
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: '#10B981',
                      mb: 2,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      fontSize: '0.75rem'
                    }}
                  >
                    Key Information
                  </Typography>
                  <Stack
                    spacing={0}
                    divider={
                      <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)' }} />
                    }
                  >
                    <DetailRow label="Issuer" value={data?.issuer} />
                    <DetailRow
                      label="Token Amount"
                      value={data?.dcn_amount?.toLocaleString()}
                    />
                    <DetailRow
                      label="Face Value"
                      value={
                        data?.face_value
                          ? `$${data.face_value.toLocaleString()}`
                          : 'N/A'
                      }
                    />
                    <DetailRow label="Currency" value={data?.currency} />
                    <DetailRow label="Instrument Type" value={data?.type} />
                    <DetailRow label="Seniority" value={data?.seniority} />
                    <DetailRow
                      label="Yield Type (Code)"
                      value={resolveLabel(
                        data?.yield_type_code,
                        YIELD_TYPE_CODES
                      )}
                    />

                    <DetailRow
                      label="Yield Rate Bucket"
                      value={resolveLabel(
                        data?.yield_rate_code,
                        YIELD_RATE_CODES
                      )}
                    />

                    <DetailRow
                      label="Redemption Frequency (Code)"
                      value={resolveLabel(
                        data?.yield_redemption_code,
                        REDEMPTION_FREQUENCY_CODES
                      )}
                    />

                    <DetailRow
                      label="YTM Type (Code)"
                      value={resolveLabel(data?.ytm_type_code, YTM_CODES)}
                    />
                    <DetailRow
                      label="Mandatory Hold Period (Code)"
                      value={resolveLabel(
                        data?.mandatory_hold_code,
                        MANDATORY_HOLD_CODES
                      )}
                    />

                    <DetailRow
                      label="Early Redemption Type (Code)"
                      value={resolveLabel(
                        data?.early_redemption_code,
                        EARLY_REDEMPTION_CODES
                      )}
                    />

                    <DetailRow
                      label="Conversion Mechanism (Code)"
                      value={resolveLabel(
                        data?.conversion_mechanism_code,
                        CONVERSION_MECHANISM_CODES
                      )}
                    />

                    <DetailRow
                      label="Conversion Window (Code)"
                      value={resolveLabel(
                        data?.conversion_type_code,
                        CONVERSION_WINDOW_CODES
                      )}
                    />
                    {/* <DetailBlock
                      label="Grade Reference"
                      value={data?.grade_reference}
                    />

                    <DetailBlock
                      label="Exemption Reference"
                      value={data?.exemption_reference}
                    />

                    <DetailBlock
                      label="Region Reference"
                      value={data?.region_reference}
                    />

                    <DetailBlock
                      label="Sector Reference"
                      value={data?.sector_reference}
                    /> */}

                    <DetailRow
                      label="Status"
                      value={data?.status === 1 ? 'Active' : 'Inactive'}
                      color={data?.status === 1 ? '#10B981' : '#EF5350'}
                    />
                  </Stack>
                </Box>

                {/* Yield Details */}
                <Box
                  sx={{
                    border: '0px solid #10B981',
                    borderRadius: 2,
                    p: 2.5,
                    backgroundColor: 'rgba(16, 185, 129, 0.03)'
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: '#10B981',
                      mb: 2,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      fontSize: '0.75rem'
                    }}
                  >
                    Yield & Returns
                  </Typography>
                  <Stack
                    spacing={0}
                    divider={
                      <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)' }} />
                    }
                  >
                    <DetailRow label="Yield Type" value={data?.yield_type} />
                    <DetailRow
                      label="Annual Yield"
                      value={
                        data?.annual_yield ? `${data.annual_yield}%` : 'N/A'
                      }
                    />
                    <DetailRow
                      label="Daily Yield"
                      value={
                        data?.daily_yield
                          ? `${data.daily_yield.toFixed(5)}%`
                          : 'N/A'
                      }
                    />
                    <DetailRow
                      label="Hourly Yield"
                      value={
                        data?.hourly_yield ? `${data.hourly_yield}%` : 'N/A'
                      }
                    />
                    <DetailRow
                      label="Yield to Maturity"
                      value={
                        data?.yield_to_maturity
                          ? `${data.yield_to_maturity}%`
                          : 'N/A'
                      }
                    />
                    <DetailRow
                      label="Redemption"
                      value={data?.yield_redemption}
                    />
                  </Stack>
                </Box>

                {/* Maturity & Terms */}
                <Box
                  sx={{
                    border: '0px solid #10B981',
                    borderRadius: 2,
                    p: 2.5,
                    backgroundColor: 'rgba(16, 185, 129, 0.03)'
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: '#10B981',
                      mb: 2,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      fontSize: '0.75rem'
                    }}
                  >
                    Terms & Conditions
                  </Typography>
                  <Stack
                    spacing={0}
                    divider={
                      <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)' }} />
                    }
                  >
                    <DetailRow label="Expiry Type" value={data?.expiry_type} />
                    <DetailRow
                      label="Maturity Date"
                      value={data?.maturity_date || 'Perpetual'}
                    />
                    <DetailRow
                      label="Early Redemption"
                      value={data?.early_redemption}
                    />
                    <DetailRow
                      label="Mandatory Hold"
                      value={
                        data?.mandatory_hold
                          ? `${data.mandatory_hold} Months`
                          : 'N/A'
                      }
                    />
                    <DetailRow
                      label="Conversion Option"
                      value={
                        data?.conversion_option
                          ? `${data.conversion_option} Months`
                          : 'N/A'
                      }
                    />
                  </Stack>
                </Box>

                {/* Action Buttons */}
                <Stack spacing={2}>
                  <Button
                    variant="outlined"
                    color="success"
                    href="https://pecuscan.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      borderRadius: 2,
                      py: 1.5,
                      borderWidth: 2,
                      fontWeight: 600,
                      '&:hover': {
                        borderWidth: 2
                      }
                    }}
                  >
                    View on Pecuscan
                  </Button>

                  {token?.link && (
                    <Button
                      variant="contained"
                      href={token.link}
                      target="_blank"
                      rel="noreferrer"
                      sx={{
                        borderRadius: 2,
                        py: 1.5,
                        backgroundColor: '#10B981',
                        color: 'white',
                        fontWeight: 600,
                        '&:hover': {
                          backgroundColor: '#059669'
                        }
                      }}
                    >
                      Learn More
                    </Button>
                  )}
                </Stack>
              </Paper>
            </Grid>

            {/* Right Column - Chart and Extended Details */}
            <Grid
              item
              xs={12}
              md={7}
              sx={{ marginTop: { xs: 4, md: 4 }, order: { xs: 0, md: 1 } }}
            >
              <Paper
                elevation={0}
                sx={{
                  backgroundColor: 'rgba(4, 10, 8, 0.72)',
                  borderRadius: 3,
                  p: 3,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 3
                }}
              >
                {/* Chart Component */}
                <Box sx={{ minHeight: 400 }}>
                  <ProChart symbol={identitySymbol} chartSymbol={token?.reference_symbol || token?.symbol} />
                </Box>

                <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

                {/* Description */}
                {token?.description && (
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{ color: '#10B981', mb: 2, fontWeight: 700 }}
                    >
                      About
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: '#f0f0f0', lineHeight: 1.8 }}
                    >
                      {token.description}
                    </Typography>
                  </Box>
                )}

                <TreasuryBackingPublic tokenType="DCN" tokenId={token?.id} />

                {/* Technical Details */}
                <Box
                  sx={{
                    border: '0px solid #10B981',
                    borderRadius: 2,
                    p: 2.5,
                    backgroundColor: 'rgba(16, 185, 129, 0.03)'
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: '#10B981',
                      mb: 2.5,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      fontSize: '0.75rem'
                    }}
                  >
                    Technical Details
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <DetailBlock
                        label="Contract Reference"
                        value={data?.contract_reference}
                      />
                    </Grid>
                    {/* <Grid item xs={12} md={6}>
                      <DetailBlock
                        label="Staking Wallet"
                        value={data?.staking_wallet}
                      />
                    </Grid> */}

                    <Grid item xs={12} md={6}>
                      <DetailBlock
                        label="Conversion Parameters"
                        value={data?.conversion_parameters}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DetailBlock
                        label="Collateral Backing"
                        value={data?.collateral_backing}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DetailBlock
                        label="Digital Asset Treasury"
                        value={data?.digital_asset_treasury}
                      />
                    </Grid>
                  </Grid>
                </Box>

                {/* Additional References */}
                <Box
                  sx={{
                    border: '0px solid #10B981',
                    borderRadius: 2,
                    p: 2.5,
                    backgroundColor: 'rgba(16, 185, 129, 0.03)'
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: '#10B981',
                      mb: 2.5,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      fontSize: '0.75rem'
                    }}
                  >
                    References & Compliance
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <DetailBlock
                        label="Grade Reference"
                        value={data?.grade_reference}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DetailBlock
                        label="Exemption Reference"
                        value={data?.exemption_reference}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DetailBlock
                        label="Region Reference"
                        value={data?.region_reference}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DetailBlock
                        label="Sector Reference"
                        value={data?.sector_reference}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DetailBlock
                        label="Created At"
                        value={
                          data?.created_at
                            ? new Date(data.created_at).toLocaleDateString(
                                'en-US',
                                {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                }
                              )
                            : 'N/A'
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DetailBlock
                        label="Updated At"
                        value={
                          data?.updated_at
                            ? new Date(data.updated_at).toLocaleDateString(
                                'en-US',
                                {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                }
                              )
                            : 'N/A'
                        }
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
            </Grid>

            {/* Transactions Table - Full Width */}
            <Grid item xs={12} mt={2} sx={{ order: { xs: 2, md: 2 } }}>
              <Transactions />
            </Grid>
          </Grid>
        </Box>
      )}
    </>
  );
}
